import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS } from "@lib/shared_constants";

/** Status filter for verified domains list */
export type VerifiedDomainStatus =
  | "all"
  | "new"
  | "active"
  | "pending_validation"
  | "action_required"
  | "expired"
  | "error";

export interface GetCompaniesCompanyIdVerifiedDomainsParams {
  /** Filter by status. Default "all" */
  status?: VerifiedDomainStatus;
  /** Start index (0-based). Default 0 */
  start?: number;
  /** Items per page. Default 10 */
  limit?: number;
}

export interface VerifiedDomain {
  id: string;
  rootDomain: string;
  domain: string;
  status: string;
  tls_status?: string;
  type: string;
  created_at?: number;
  expires_at?: number;
  activated_at?: number;
  dns_records?: VerifiedDomainDnsRecord[];
  custom_domains?: VerifiedDomain[];
  ns_verified?: boolean;
  txt_verified?: boolean;
  channel?: string;
  workspace_count?: number;
}

export interface VerifiedDomainDnsRecord {
  type: string;
  name: string;
  value: string;
  status: string;
  found_value?: string;
  /** See `Braze::CustomDomains::TxtVerificationErrorReason`. Independent of `status`. */
  error_reason?: string;
}

/** The company's verified (delegated) domain cap, as counted by the backend. */
export interface VerifiedDomainLimits {
  /** Delegated domains currently occupying a slot (live statuses, including `error`) */
  current: number;
  /** Company-wide cap. 0 when no verified domains have been provisioned for the company */
  max: number;
}

export interface GetCompaniesCompanyIdVerifiedDomainsResult {
  hits: number;
  results: VerifiedDomain[];
  /** True when the company has at least one Amazon SES ESP account */
  has_ses_account: boolean;
  /** True when the company has any non-SES ESP account (SendGrid/SparkPost/etc.) */
  has_non_ses_esp_account: boolean;
  /** Verified domain usage against the company cap, independent of the status filter and pagination */
  limits: VerifiedDomainLimits;
}

/** Domain type for creating a verified domain (e.g. delegated_domain) */
export type VerifiedDomainType = "delegated_domain";

export interface PostCompaniesCompanyIdVerifiedDomainsProps {
  companyId: string;
  type: VerifiedDomainType;
  subdomain: string;
  rootDomain: string;
}

export type PostCompaniesCompanyIdVerifiedDomainsResult = VerifiedDomain;

export type PostCompaniesCompanyIdVerifiedDomainsResultMissingTypeError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["MISSING_TYPE"],
  { parameter_name: "type" }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultUnsupportedTypeError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["UNSUPPORTED_TYPE"],
  { domain_type: string }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultMissingSubdomainError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["MISSING_SUBDOMAIN"],
  { parameter_name: "subdomain" }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultMissingRootDomainError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["MISSING_ROOT_DOMAIN"],
  { parameter_name: "root_domain" }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultInvalidRootDomainError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["INVALID_ROOT_DOMAIN"],
  { root_domain: string }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultInvalidDomainError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["INVALID_DOMAIN"],
  { subdomain: string; root_domain: string }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultDomainAlreadyExistsError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["ALREADY_EXISTS"],
  { domain: string }
>;

/** The name is already configured as one of the company's email sending domains. */
export type PostCompaniesCompanyIdVerifiedDomainsResultSendingDomainConflictError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["SENDING_DOMAIN_CONFLICT"],
  { domain: string }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultDelegatedDomainLimitReachedError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["DELEGATED_DOMAIN_LIMIT_REACHED"],
  { current: number; max: number }
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultSesRequiredError = FetchUserEncodedError<
  SHARED_CONSTANTS_VERIFIED_DOMAIN_ERRORS["SES_REQUIRED"],
  Record<string, never>
>;

export type PostCompaniesCompanyIdVerifiedDomainsResultErrors =
  | PostCompaniesCompanyIdVerifiedDomainsResultMissingTypeError
  | PostCompaniesCompanyIdVerifiedDomainsResultUnsupportedTypeError
  | PostCompaniesCompanyIdVerifiedDomainsResultMissingSubdomainError
  | PostCompaniesCompanyIdVerifiedDomainsResultMissingRootDomainError
  | PostCompaniesCompanyIdVerifiedDomainsResultInvalidRootDomainError
  | PostCompaniesCompanyIdVerifiedDomainsResultInvalidDomainError
  | PostCompaniesCompanyIdVerifiedDomainsResultDomainAlreadyExistsError
  | PostCompaniesCompanyIdVerifiedDomainsResultSendingDomainConflictError
  | PostCompaniesCompanyIdVerifiedDomainsResultDelegatedDomainLimitReachedError
  | PostCompaniesCompanyIdVerifiedDomainsResultSesRequiredError;

export const isPostCompaniesCompanyIdVerifiedDomainsResultError = (
  error: FetchRequestError
): error is PostCompaniesCompanyIdVerifiedDomainsResultErrors => {
  return error instanceof FetchUserEncodedError;
};
